/**
 * Replicar o mapeamento de uma seção para as outras seções do template.
 *
 * O template do cliente costuma ter uma seção por agrupamento, todas com as
 * mesmas colunas: na planilha da Colgate são 18 seções × 6 colunas, uns 108
 * mapeamentos criados um a um no diálogo. No dev, o PME do SOMOS parou em 2
 * das 20 seções. Aqui a pessoa mapeia UMA seção e copia para as demais, e cada
 * cópia só troca o que muda de uma seção para outra: a linha inicial e o
 * agrupamento.
 *
 * Na planilha com uma aba por agrupamento (a da Fresenius: AÉREO, TRANSFER,
 * HOSPEDAGENS…, todas com os itens nas linhas 5 a 16), o destino é o MESMO
 * bloco nas outras abas, e a cópia troca também a aba e o texto do cabeçalho.
 *
 * Vive fora do componente para ter teste direto, como o
 * `mapping-apply.util.ts` na Api.
 */
import {
  BudgetGroupTemplateEntry,
  FieldMapping,
  SheetInspection,
  TemplateSection,
} from "../../../../../types/clientSpreadsheet.type";
import { normalizeGroupName } from "../../../../../utils/budget-groups";

/** O mesmo `getStartRow` da Api: sem `startRow`, a linha abaixo do cabeçalho. */
export function startRowOf(mapping: Partial<FieldMapping>): number {
  const headerRow = mapping.headerRow ?? 1;
  return mapping.startRow ?? (headerRow > 0 ? headerRow + 1 : 2);
}

/**
 * Coluna de serviço recortada por agrupamento — o único mapeamento que faz
 * sentido replicar. Sem agrupamento, toda seção receberia todos os serviços.
 */
export function isReplicableMapping(mapping: Partial<FieldMapping>): boolean {
  return (
    mapping.type === "column" &&
    mapping.source === "arrayItem" &&
    !!mapping.arrayField &&
    !!normalizeGroupName(mapping.group)
  );
}

export interface MappingBlock {
  sheetName: string;
  startRow: number;
  group: string;
  /** Posições, na lista de mapeamentos, das colunas do bloco. */
  indexes: number[];
}

/**
 * As colunas do bloco a que o mapeamento pertence: mesma aba, mesma linha
 * inicial, mesmo agrupamento. É a mesma identidade de bloco da geração
 * (`getTableExpansionKey`), menos a categoria — um bloco pode misturar várias.
 */
export function blockOf(
  mappings: FieldMapping[],
  index: number
): MappingBlock | null {
  const source = mappings[index];
  if (!source || !isReplicableMapping(source)) return null;

  const startRow = startRowOf(source);
  const group = normalizeGroupName(source.group);
  const indexes = mappings
    .map((mapping, i) => ({ mapping, i }))
    .filter(
      ({ mapping }) =>
        isReplicableMapping(mapping) &&
        mapping.sheetName === source.sheetName &&
        mapping.arrayField === source.arrayField &&
        startRowOf(mapping) === startRow &&
        normalizeGroupName(mapping.group) === group
    )
    .map(({ i }) => i);

  return {
    sheetName: source.sheetName,
    startRow,
    group: (source.group ?? "").trim(),
    indexes,
  };
}

/**
 * O título da seção sem as observações que o cliente escreve embaixo dele. Na
 * Colgate, o título do AÉREO é "AÉREO" seguido de três linhas de aviso sobre
 * tarifa — ninguém quer isso como nome de agrupamento no orçamento.
 */
export function sectionTitle(name: string): string {
  return (name ?? "").split(/\r?\n/)[0].trim();
}

export function sectionContainingRow(
  sections: TemplateSection[],
  row: number
): TemplateSection | undefined {
  return sections.find(
    (section) => row >= section.startRow && row <= section.endRow
  );
}

/**
 * Agrupamento que a seção recebe: o do template de agrupamentos do cliente
 * que tem o nome dela (inteiro ou só o título) — é o que o orçamento novo vai
 * ter. Sem entrada correspondente, o título da seção.
 *
 * Seção de OUTRA aba (`sheetName`) é procurada pelo nome da aba: o título se
 * repete entre as abas — "Tipo de EQUIPAMENTOS" em sete abas da Fresenius — e
 * daria o mesmo agrupamento a todas.
 */
export function defaultGroupFor(
  section: TemplateSection,
  templateEntries: BudgetGroupTemplateEntry[] | undefined,
  sheetName?: string
): string {
  const title = sectionTitle(section.name);
  const keys = sheetName
    ? [normalizeGroupName(sheetName)]
    : [normalizeGroupName(section.name), normalizeGroupName(title)];
  const entry = (templateEntries ?? []).find((candidate) =>
    keys.includes(normalizeGroupName(candidate.name))
  );
  if (entry) return entry.name.trim();
  return sheetName ? sheetName.trim() : title;
}

/**
 * O mesmo bloco nas outras abas: mesma linha inicial e final. É o que a
 * planilha com uma aba por agrupamento tem — cada aba é cópia da outra. Bloco
 * de outro tamanho é outra coisa (o "RESUMO DE VALORES" da Fresenius, as abas
 * LOGISTICA e PRODUÇÃO da Merz) e não entra.
 */
export function sameBlockInOtherSheets(
  sheets: SheetInspection[],
  sourceSheetName: string,
  sourceSection: TemplateSection | undefined
): Array<{ sheetName: string; section: TemplateSection }> {
  if (!sourceSection) return [];
  return sheets
    .filter((sheet) => sheet.name !== sourceSheetName)
    .flatMap((sheet) =>
      (sheet.sections ?? [])
        .filter(
          (section) =>
            section.startRow === sourceSection.startRow &&
            section.endRow === sourceSection.endRow
        )
        .map((section) => ({ sheetName: sheet.name, section }))
    );
}

const sameHeaderText = (a: string, b: string) =>
  a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * Coluna da cópia em outra aba: a MESMA letra, com o texto que o cabeçalho
 * tem lá. A Api acha a coluna pelo texto, e ele muda de aba para aba — D4 é
 * "Check-in 16h00" no AÉREO e "Descrição" no EQUIPAMENTOS, onde E4 também é
 * "Descrição". Por isso a letra vai sempre: sem ela, o texto repetido cairia
 * na última ocorrência.
 *
 * Só quando as duas abas têm o cabeçalho detectado na linha do mapeamento; fora
 * disso a inspeção não sabe dizer e a coluna vai como na origem — a validação
 * do template aponta se não existir lá.
 */
function columnInSheet(
  mapping: FieldMapping,
  sourceSheet: SheetInspection | undefined,
  targetSheet: SheetInspection | undefined
): Pick<FieldMapping, "columnHeader" | "columnLetter"> | null {
  const headerRow = mapping.headerRow ?? 1;
  if (
    !sourceSheet ||
    !targetSheet ||
    sourceSheet.headerRow !== headerRow ||
    targetSheet.headerRow !== headerRow
  ) {
    return null;
  }

  const letter =
    mapping.columnLetter?.trim().toUpperCase() ||
    [...sourceSheet.headers]
      .reverse()
      .find((header) =>
        sameHeaderText(header.value, mapping.columnHeader ?? "")
      )?.letter;
  const header = targetSheet.headers.find((h) => h.letter === letter);

  return letter && header
    ? { columnHeader: header.value, columnLetter: letter }
    : null;
}

/**
 * A seção já tem alguma coluna de serviço mapeada. Replicar nela duplicaria
 * as colunas: as duas cópias escreveriam a mesma célula.
 */
export function isSectionMapped(
  mappings: FieldMapping[],
  sheetName: string,
  section: TemplateSection
): boolean {
  return mappings.some(
    (mapping) =>
      mapping.type === "column" &&
      mapping.source === "arrayItem" &&
      mapping.sheetName === sheetName &&
      startRowOf(mapping) >= section.startRow &&
      startRowOf(mapping) <= section.endRow
  );
}

function sameText(a: unknown, b: unknown): boolean {
  return (
    typeof a === "string" &&
    typeof b === "string" &&
    normalizeGroupName(a) === normalizeGroupName(b)
  );
}

/**
 * Rótulo da cópia. Com o nome do agrupamento de origem dentro, só ele é
 * trocado. Sem — na BRF os rótulos são "Instagramavel - Qtde", sem acento nem
 * número —, a cópia vira "agrupamento - campo": manter o rótulo da origem
 * mostraria "Instagramavel - Qtde" na linha do "4 - ÁREA FOOD".
 */
function relabel(
  label: string | undefined,
  fromGroup: string,
  toGroup: string,
  fieldName: string | undefined
): string | undefined {
  if (!label) return label;
  const at = fromGroup
    ? label
        .toLocaleLowerCase("pt-BR")
        .indexOf(fromGroup.toLocaleLowerCase("pt-BR"))
    : -1;
  if (at !== -1) {
    return label.slice(0, at) + toGroup + label.slice(at + fromGroup.length);
  }
  return fieldName ? `${toGroup} - ${fieldName}` : label;
}

export interface ReplicationTarget {
  section: TemplateSection;
  group: string;
  /** Aba de destino, quando não é a da origem. */
  sheet?: SheetInspection;
}

/**
 * As colunas do bloco de origem, uma cópia por seção de destino.
 *
 * O cabeçalho tem dois arranjos, e a cópia segue o da origem:
 * - ÚNICO para a aba (Colgate na linha 8, PME do SOMOS na 5): o `headerRow`
 *   fica como está;
 * - um POR SEÇÃO, na linha do título (BRF): o `headerRow` vira o da seção de
 *   destino. Na BRF a coluna do serviço tem como cabeçalho o PRÓPRIO título da
 *   seção ("3 - INSTAGRAMÁVEL" em C38) — esse cabeçalho vira o título do
 *   destino.
 *
 * Categoria, letra da coluna e campo vêm iguais: as seções têm as mesmas
 * colunas. `fieldLabels` (caminho -> nome do campo) só serve ao rótulo.
 *
 * Em outra aba, a cópia vai para ela, e a coluna sai de `columnInSheet`;
 * `sourceSheet` é a inspeção da aba de origem, para achar a letra.
 */
export function replicateBlock(
  blockMappings: FieldMapping[],
  sourceSection: TemplateSection | undefined,
  targets: ReplicationTarget[],
  fieldLabels: Record<string, string> = {},
  sourceSheet?: SheetInspection
): FieldMapping[] {
  return targets.flatMap(({ section, group, sheet }) =>
    blockMappings.map((mapping) => {
      const perSectionHeader =
        !!sourceSection && (mapping.headerRow ?? 1) === sourceSection.headerRow;
      const headerIsTitle =
        perSectionHeader && sameText(mapping.columnHeader, sourceSection?.name);

      const copy: FieldMapping = {
        ...mapping,
        startRow: section.startRow,
        headerRow: perSectionHeader ? section.headerRow : mapping.headerRow,
        columnHeader: headerIsTitle ? section.name : mapping.columnHeader,
        group: group.trim(),
      };

      if (sheet) {
        copy.sheetName = sheet.name;
        const column = columnInSheet(mapping, sourceSheet, sheet);
        if (column) Object.assign(copy, column);
      }

      const label = relabel(
        mapping.label,
        (mapping.group ?? "").trim(),
        group.trim(),
        fieldLabels[mapping.ourField]
      );
      if (label === undefined) delete copy.label;
      else copy.label = label;

      return copy;
    })
  );
}
